import axios, { type AxiosInstance, type AxiosRequestConfig } from "axios";
import type { TenantConfig } from "@/types";

export enum ApiSource {
  PRIMARY = "primary",
  SECONDARY = "secondary",
}

interface ApiConfig {
  primaryUrl: string;
  secondaryUrl: string;
  useUnified: boolean;
}

const config: ApiConfig = {
  // 生产打包未配置时用空字符串 = 相对当前页面域名；开发见 .env.development
  primaryUrl: import.meta.env.VITE_API_BASE_URL ?? "",
  secondaryUrl:
    import.meta.env.VITE_API_SECONDARY_URL ??
    import.meta.env.VITE_API_BASE_URL ??
    "",
  useUnified: import.meta.env.VITE_USE_UNIFIED_API === "true",
}

function createApiInstance(baseURL: string): AxiosInstance {
  const instance = axios.create({
    baseURL,
    timeout: 10000,
  });
  instance.interceptors.response.use(
    (response) => response.data,
    (error) => {
      console.error("API Error:", error);
      return Promise.reject(error);
    }
  );
  return instance;
}

const primaryApi = createApiInstance(config.primaryUrl);
const secondaryApi = createApiInstance(
  config.useUnified ? config.primaryUrl : config.secondaryUrl
);

export function getApi(source: ApiSource = ApiSource.PRIMARY): AxiosInstance {
  if (config.useUnified) {
    return primaryApi;
  }
  return source === ApiSource.PRIMARY ? primaryApi : secondaryApi;
}

export async function request<T = any>(
  url: string,
  options?: AxiosRequestConfig & { source?: ApiSource }
): Promise<T> {
  const { source = ApiSource.PRIMARY, ...axiosConfig } = options || {};
  const api = getApi(source);
  return api.request({ url, ...axiosConfig });
}

export async function fetchTenantConfig(
  tenantId: string
): Promise<TenantConfig> {
  try {
    const response = await primaryApi.get(`/tenants/${tenantId}/config`);
    return response as unknown as TenantConfig;
  } catch (error) {
    if (import.meta.env.DEV) {
      return getDefaultConfig(tenantId);
    }
    throw error;
  }
}

function getDefaultConfig(tenantId: string): TenantConfig {
  return {
    tenantId,
    name: "默认租户",
    domain: "example.com",
    theme: {
      primaryColor: "#409EFF",
      secondaryColor: "#67C23A",
      backgroundColor: "#FFFFFF",
      textColor: "#303133",
      borderColor: "#DCDFE6",
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      borderRadius: "4px",
      shadows: true,
    },
    features: {
      showLogo: true,
      showBreadcrumb: true,
      showFooter: true,
      availableModules: [
        "investor-relations",
        "financial-data",
        "announcements",
      ],
    },
    customization: {
      headerText: "投资者关系管理系统",
      footerText: "© 2024 IRM Widget",
      disclaimerText: "本系统提供的信息仅供参考",
      contactInfo: {
        email: "ir@example.com",
        phone: "+86-400-123-4567",
      },
      socialLinks: [],
    },
  };
}

export { primaryApi, secondaryApi, config as apiConfig };
export const api = primaryApi;
